import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';

export interface RecipeStepsProps {
  /** The vendor's instruction text, one entry per step, already in order. */
  steps: string[];
  className?: string;
}

/**
 * A recipe's preparation, as a numbered list carrying equal visual weight with the ingredients
 * beside it — neither is a sidebar of the other.
 *
 * A recipe with no steps renders nothing at all rather than a placeholder.
 */
export function RecipeSteps({ steps, className }: RecipeStepsProps) {
  const { t } = useTranslation('shared');

  if (steps.length === 0) return null;

  return (
    <section data-slot="recipe-steps" className={cn('flex flex-col gap-4', className)}>
      <h2 className="text-h4 font-semibold text-foreground">{t('recipes.detail.preparation.title')}</h2>
      <ol className="flex flex-col gap-5">
        {steps.map((step, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: step order is the step's identity
          <li key={index} className="flex gap-4">
            <span
              aria-hidden="true"
              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-foreground"
            >
              {index + 1}
            </span>
            <div className="flex min-w-0 flex-col gap-1 pt-1">
              <p className="text-base leading-relaxed text-foreground">
                <span className="sr-only">{t('recipes.detail.preparation.stepLabel', { number: index + 1 })} </span>
                {step}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
